import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import LoadingSpinner from 'components/LoadingSpinner';
import EditIcon from 'assets/edit_outlined.svg?react';
import DownloadIcon from 'assets/download_outlined_green.svg?react';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import { paths } from 'routes/paths';
import MessageContent from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/components/MessageContent';
import { RECORD_SOURCE_MESSAGE_KEY } from 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/components/RecordSourceButton/constants/messageKey';
import showRecordBanner from 'screens/Applications&WorkOrders/Components/showRecordBanner';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { getLinkedRecordDescriptor } from '../../../../typeDescriptors';
import { LinkedRecordRow } from '../../../../types';

/**
 * Hard-coded rather than `text-green-50`/`60`: this palette's `green-50` is a
 * pale mint, so the Tailwind classes of those names are different colours.
 */
const PDF_IDLE_COLOR = '#14803C';
const PDF_LOADING_TEXT_COLOR = '#19A04B';
const DOWNLOAD_BUTTON_WIDTH_PX = 150;
const ICON_SIZE_PX = 18;

const BUTTON_STYLE: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  justifyContent: 'center'
};

/**
 * Inline, not Tailwind: the kit's `button > span { display: block }` outranks a
 * single utility class, so `flex` never applied.
 */
const BUTTON_LABEL_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap'
};

interface DrawerFooterProps {
  record: LinkedRecordRow;
  recommendationId: string;
  recordCropSeasonIds?: string[];
  onClose: () => void;
  onDownloadPdf?: () => void;
  isDownloadingPdf?: boolean;
}

const DrawerFooter = ({
  record,
  recommendationId,
  recordCropSeasonIds,
  onClose,
  onDownloadPdf,
  isDownloadingPdf = false
}: DrawerFooterProps) => {
  const history = useHistory();
  const {
    selectedCropSeasonIds,
    hasAllSelectedCropSeasonIds,
    setSelectedCropSeasonIds
  } = useSelectedCropSeasonIds();

  const descriptor = getLinkedRecordDescriptor(record.type);

  const handleEdit = () => {
    if (!descriptor) return;
    history.push(descriptor.editPath(record.id, record.name) as never);
    onClose();
  };

  const handleGoTo = () => {
    if (!descriptor) return;

    const previousCropSeasonIds = selectedCropSeasonIds;
    // Containment, not equality: a recommendation spanning several seasons
    // needs no change for a child inside one of them, and so has nothing to
    // restore on the way back either.
    const needsBreadcrumbUpdate =
      !!recordCropSeasonIds?.length &&
      !hasAllSelectedCropSeasonIds(recordCropSeasonIds);

    if (needsBreadcrumbUpdate) {
      setSelectedCropSeasonIds(recordCropSeasonIds as string[]);
    }

    history.push(descriptor.viewPath(record.id, record.name) as never);
    onClose();

    const onReturn = () => {
      if (needsBreadcrumbUpdate) {
        setSelectedCropSeasonIds(previousCropSeasonIds);
      }
      history.push(`${paths.recommendations}/${recommendationId}/view`);
    };

    showRecordBanner({
      key: RECORD_SOURCE_MESSAGE_KEY,
      className: 'ant-message-dark-mode br-4 record-banner-anchored',
      content: (
        <MessageContent
          label="Viewing linked record."
          returnLabel="Return to Recommendation"
          onReturn={onReturn}
        />
      ),
      duration: 0,
      icon: <></>
    });
  };

  return (
    <div
      data-testid="linked-record-drawer-footer"
      className="flex justify-between items-center border-t border-neutral-20 px-6 py-5"
    >
      <Button
        type={ButtonType.outline}
        onClick={onClose}
        size="large"
        data-testid="linked-record-drawer-close"
        style={BUTTON_STYLE}
      >
        Close
      </Button>
      <div className="flex gap-2">
        <Button
          type={ButtonType.outline}
          onClick={handleEdit}
          size="large"
          data-testid="linked-record-drawer-edit"
          style={BUTTON_STYLE}
        >
          <span style={BUTTON_LABEL_STYLE}>
            <EditIcon
              width={ICON_SIZE_PX}
              height={ICON_SIZE_PX}
              className="shrink-0"
            />
            Edit
          </span>
        </Button>
        <Button
          type={ButtonType.outline}
          onClick={isDownloadingPdf ? undefined : onDownloadPdf}
          size="large"
          aria-busy={isDownloadingPdf}
          aria-disabled={isDownloadingPdf}
          data-testid="linked-record-drawer-download-pdf"
          style={{
            ...BUTTON_STYLE,
            color: PDF_IDLE_COLOR,
            borderColor: PDF_IDLE_COLOR,
            width: DOWNLOAD_BUTTON_WIDTH_PX
          }}
        >
          {isDownloadingPdf ? (
            <span style={{ color: PDF_LOADING_TEXT_COLOR }}>
              <LoadingSpinner inline size={ICON_SIZE_PX} message="Loading..." />
            </span>
          ) : (
            <span style={BUTTON_LABEL_STYLE}>
              <DownloadIcon
                width={ICON_SIZE_PX}
                height={ICON_SIZE_PX}
                className="shrink-0"
              />
              Download PDF
            </span>
          )}
        </Button>
        <Button
          type={ButtonType.primary}
          onClick={handleGoTo}
          size="large"
          data-testid="linked-record-drawer-go-to"
          style={BUTTON_STYLE}
        >
          <span style={BUTTON_LABEL_STYLE}>
            <OpenInNewIcon
              width={ICON_SIZE_PX}
              height={ICON_SIZE_PX}
              className="shrink-0"
            />
            {descriptor?.goToActionLabel ?? 'Go to Record'}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default DrawerFooter;
